import {
  DELIVERY_CHANNELS,
  HANDS_FREE_DELIVERY_STATUSES,
  HANDS_FREE_STATUSES,
  RESPONSE_MODES,
  type DeliveryChannel,
  type HandsFreeAction,
  type HandsFreeScript,
  type HandsFreeState,
  type HandsFreeStatus,
  type ResponseMode,
} from '@/shared-responder-v2-core';

import type { ResponderSynthesisRequest } from './shared-responder-v2-capability-coordinator.types';
import { joinTranscript } from './shared-responder-v2-dictation.helpers';

export const HANDS_FREE_DISARMED_STATUSES: ReadonlySet<HandsFreeStatus> =
  new Set([HANDS_FREE_STATUSES.Dormant, HANDS_FREE_STATUSES.Ended]);

export const HANDS_FREE_ACTIVE_MEDIA_STATUSES: ReadonlySet<HandsFreeStatus> =
  new Set([
    HANDS_FREE_STATUSES.Speaking,
    HANDS_FREE_STATUSES.Listening,
    HANDS_FREE_STATUSES.Capturing,
    HANDS_FREE_STATUSES.Captured,
    HANDS_FREE_STATUSES.Paused,
  ]);

export function isHandsFreeVoiceDelivering(args: {
  readonly isArmed: boolean;
  readonly isMuted: boolean;
  readonly status: HandsFreeStatus;
  readonly mode: ResponseMode;
}): boolean {
  return (
    args.isArmed &&
    !args.isMuted &&
    args.mode !== RESPONSE_MODES.Text &&
    HANDS_FREE_DELIVERY_STATUSES.has(args.status)
  );
}

export const HANDS_FREE_LOOP_GATES = {
  End: 'end',
  SoftStandDown: 'softStandDown',
  Arm: 'arm',
  Run: 'run',
} as const;

export type HandsFreeLoopGate =
  (typeof HANDS_FREE_LOOP_GATES)[keyof typeof HANDS_FREE_LOOP_GATES];

export function resolveHandsFreeLoopGate(args: {
  readonly inQuestionHandsFree: boolean;
  readonly isCaptureReady: boolean;
  readonly status: HandsFreeStatus;
}): HandsFreeLoopGate {
  if (!args.inQuestionHandsFree) {
    return HANDS_FREE_LOOP_GATES.End;
  }

  if (!args.isCaptureReady) {
    return HANDS_FREE_LOOP_GATES.SoftStandDown;
  }

  return HANDS_FREE_DISARMED_STATUSES.has(args.status)
    ? HANDS_FREE_LOOP_GATES.Arm
    : HANDS_FREE_LOOP_GATES.Run;
}

/**
 * Receives hands-free as already-resolved verdicts, since this port is
 * schema-free and each adapter narrows its own enums.
 *
 * `project.mode` is not enough on its own. It is deprecated, only the V2
 * builder's own interaction control writes it, and it writes it *alongside*
 * `isAiVoiceEnabled` — so a voice project whose `mode` was never migrated (or
 * was set through any other path) came back as `chat`, the hands-free loop
 * never armed, and the AI never spoke a word for the whole session. The second
 * clause reads the pair the builder actually writes: the AI moderates when it
 * is allowed to speak and the project is answered by voice.
 *
 * A V2 project's `interactionType` is its only voice setting: Companion writes
 * it without touching `mode` or `isAiVoiceEnabled`, so when it is authoritative
 * it alone decides. Legacy projects keep the pair above, because the legacy
 * settings form changes `mode` without rewriting `interactionType`.
 */
export function resolveDeliveryChannel(args: {
  readonly isHandsFree: boolean;
  readonly isAiVoiceEnabled: boolean;
  readonly projectInteractionType: ResponseMode;
  readonly isInteractionTypeAuthoritative: boolean;
}): DeliveryChannel {
  if (args.isInteractionTypeAuthoritative) {
    return args.projectInteractionType === RESPONSE_MODES.Voice
      ? DELIVERY_CHANNELS.HandsFree
      : DELIVERY_CHANNELS.Chat;
  }

  const isAiModerated =
    args.isHandsFree ||
    (args.isAiVoiceEnabled &&
      args.projectInteractionType === RESPONSE_MODES.Voice);

  return isAiModerated ? DELIVERY_CHANNELS.HandsFree : DELIVERY_CHANNELS.Chat;
}

const COMPOSITE_KEY_SEPARATOR = '\u001f';

export function toCompositeKey(parts: readonly string[]): string {
  return parts.join(COMPOSITE_KEY_SEPARATOR);
}

export function toHandsFreeSpeakRequest(
  script: HandsFreeScript,
  language?: string
): ResponderSynthesisRequest {
  return {
    cacheKey: script.cacheKey,
    text: script.text.trim() === '' ? '' : script.text,
    language,
  };
}

// A control character no recognised transcript can contain, so decoding a
// plain string that never went through `encodeUtteranceCommit` is the
// identity.
const UTTERANCE_COMMIT_SEPARATOR = '\u0000';

/**
 * A same-value `setState` is a no-op React never re-renders for, so an
 * identical utterance — the same continue word retried once a site gate
 * opens, the same option said twice — would otherwise never reach the loop a
 * second time. The sequence makes every commit distinct regardless of the
 * words spoken; recognising a genuine repeat as a repeat is left entirely to
 * the machine that reads the decoded text back out.
 */
export function encodeUtteranceCommit(sequence: number, text: string): string {
  return `${sequence}${UTTERANCE_COMMIT_SEPARATOR}${text}`;
}

export function decodeUtteranceCommit(commit: string): string {
  const separatorIndex = commit.indexOf(UTTERANCE_COMMIT_SEPARATOR);

  return separatorIndex === -1 ? commit : commit.slice(separatorIndex + 1);
}

function resolveActionDetail(action: HandsFreeAction): string {
  if (action.type === 'speak') {
    return action.script.cacheKey;
  }

  return action.type === 'draft' || action.type === 'draftThenAdvance'
    ? action.event.type
    : '';
}

/**
 * Identifies one run of the loop. The loop performs an action when this key
 * changes, so a repeated `listen` does not re-arm the microphone while a
 * changing transcript still re-drafts.
 */
export function resolveHandsFreeActionKey(
  action: HandsFreeAction,
  handsFree: HandsFreeState
): string {
  const detail = resolveActionDetail(action);

  return [
    handsFree.status,
    action.type,
    detail,
    handsFree.transcript ?? '',
  ].join('|');
}

export const HANDS_FREE_SPEECH_HOLD_LIMIT_MS = 6000;

export function resolveSilenceDelayMs(args: {
  readonly isVoiceAudible: boolean;
  readonly silenceMs: number;
  readonly msSinceUtterance: number;
}): number {
  return args.isVoiceAudible
    ? Math.max(0, HANDS_FREE_SPEECH_HOLD_LIMIT_MS - args.msSinceUtterance)
    : args.silenceMs;
}

export function stripEchoedPrefix(args: {
  readonly text: string;
  readonly echoed: string;
  readonly seed: string;
}): string {
  return args.echoed !== '' && args.text.startsWith(args.echoed)
    ? joinTranscript(args.seed, args.text.slice(args.echoed.length))
    : args.text;
}
